import type {Agent} from "@atproto/api";
import {TID} from "@atproto/common-web";
import {
  AtpClientError,
  type SyncMetadata,
  stripLibraryEntryMetadata,
} from "@/app/atproto";
import {IDS, type LibraryEntry} from "@/shared/types";

export const CLOCKID = 999;

/**
 * Creates a new library entry in the user's repository.
 *
 * @param agent - The authenticated ATProto agent.
 * @param entry - The entry data (possibly including local sync metadata).
 * @param rkey - Optional specific Record Key. If omitted, a TID-based key is generated.
 * @returns The created record's URI, CID, and value.
 */
export async function createLibraryEntry(
  agent: Agent,
  entry: Omit<LibraryEntry, "createdAt" | "updatedAt"> & SyncMetadata,
  rkey?: string,
) {
  const createdAt = new Date();
  const cleanEntry = stripLibraryEntryMetadata(entry);

  const record = {
    ...cleanEntry,
    createdAt: createdAt.toISOString(),
    updatedAt: new Date().toISOString(),
    $type: IDS.LibraryEntry,
  };

  const result = await agent.com.atproto.repo.putRecord({
    repo: agent.did ?? "",
    collection: IDS.LibraryEntry,
    rkey: rkey || TID.fromTime(createdAt.getTime(), CLOCKID).toString(),
    record,
  });
  if (!result.success)
    throw new AtpClientError("library.entry", "create", "error creating new record");

  const {uri, cid} = result.data;
  return {uri, cid, value: record};
}

/**
 * Updates an existing library entry.
 *
 * @param agent - The authenticated ATProto agent.
 * @param uri - The AT-URI of the record to update.
 * @param cid - The current CID (Swap Record) to ensure concurrency safety.
 * @param entry - The updated entry data (possibly including local sync metadata).
 * @returns The updated record's URI, CID, and value.
 */
export async function updateLibraryEntry(
  agent: Agent,
  uri: string,
  cid: string,
  entry: Omit<LibraryEntry, "updatedAt"> & SyncMetadata,
) {
  // Parse rkey from uri
  const rkey = uri.split("/").pop();
  if (!rkey) throw new AtpClientError("library.entry", "update", "Invalid URI");

  const cleanEntry = stripLibraryEntryMetadata(entry);

  const record = {
    ...cleanEntry,
    updatedAt: new Date().toISOString(),
    $type: IDS.LibraryEntry,
  };

  await agent.com.atproto.repo.putRecord({
    repo: agent.did ?? "",
    collection: IDS.LibraryEntry,
    rkey,
    record,
    swapRecord: cid,
  });

  return {uri, cid, value: record};
}

/**
 * Deletes a library entry.
 *
 * @param agent - The authenticated ATProto agent.
 * @param uri - The AT-URI of the record to delete.
 */
export async function deleteLibraryEntry(agent: Agent, uri: string) {
  const rkey = uri.split("/").pop();
  if (!rkey) throw new AtpClientError("library.entry", "delete", "Invalid URI");

  await agent.com.atproto.repo.deleteRecord({
    repo: agent.did ?? "",
    collection: IDS.LibraryEntry,
    rkey,
  });
}

/**
 * Fetches a single library entry by its URI.
 *
 * @param agent - The authenticated ATProto agent.
 * @param uri - The AT-URI of the record.
 * @returns The record's URI, CID, and value.
 */
export async function getLibraryEntry(agent: Agent, uri: string) {
  // at://did:plc:1234/collection/rkey
  const [, , repo, , rkey] = uri.split("/");

  // We need to parse the rkey and repo DID from the URI
  if (!repo)
    throw new AtpClientError("library.entry", "get", "Invalid URI, can't parse DID");
  if (!rkey)
    throw new AtpClientError("library.entry", "get", "Invalid URI, can't parse rkey");

  const {data} = await agent.com.atproto.repo.getRecord({
    repo,
    collection: IDS.LibraryEntry,
    rkey,
  });

  return {
    uri: data.uri,
    cid: data.cid,
    value: data.value as unknown as LibraryEntry,
  };
}

/**
 * Lists library entries from a repository.
 *
 * @param agent - The authenticated ATProto agent.
 * @param params - Query parameters (repo, limit, cursor, reverse).
 * @returns A list of records and a cursor for pagination.
 */
export async function listLibraryEntries(
  agent: Agent,
  params?: {
    repo?: string;
    limit?: number;
    cursor?: string;
    reverse?: boolean;
  },
) {
  const {data} = await agent.com.atproto.repo.listRecords({
    repo: params?.repo ?? agent.did ?? "",
    collection: IDS.LibraryEntry,
    limit: params?.limit,
    cursor: params?.cursor,
    reverse: params?.reverse,
  });

  return {
    cursor: data.cursor,
    records: data.records.map(record => ({
      uri: record.uri,
      cid: record.cid,
      value: record.value as unknown as LibraryEntry,
    })),
  };
}
